const debugEnv = process.env.REACT_APP_DEBUG ?? ''
function envFlagTrue(v: string): boolean {
  return v === 'true' || v === '1' || v.toLowerCase() === 'yes'
}
export default {
  /**
   * Client Support page: when true, all signed-in clients see /support; when false, only `supportPageTesters`.
   */
  supportPageReleased: envFlagTrue(process.env.REACT_APP_SUPPORT_PAGE_RELEASED ?? ''),
  /** Comma-separated client user IDs allowed to see /support when it is not released. Falls back to legacy REACT_APP_CUSTOMER_PAGE_TESTERS. */
  supportPageTesters:
    process.env.REACT_APP_SUPPORT_PAGE_TESTERS ?? process.env.REACT_APP_CUSTOMER_PAGE_TESTERS ?? '',
  /** Global app debug (e.g. `[SupportPage]` console logs). `REACT_APP_DEBUG`: true | 1 | yes. Build-time. */
  debug: envFlagTrue(debugEnv),
  /**
   * Portal paywall Phase 1: when true, all clients get status/banner/redirect; when false, only `paywallTesters`.
   */
  paywallReleased: envFlagTrue(process.env.REACT_APP_PAYWALL_RELEASED ?? ''),
  /** Comma-separated clientPid (`user.id`) when paywall is not released. */
  paywallTesters: process.env.REACT_APP_PAYWALL_TESTERS ?? '',
  /** Billing API origin (no trailing slash), e.g. http://localhost:4700 or https://api… */
  billingApiBase: (process.env.REACT_APP_BILLING_API_BASE ?? '').replace(/\/+$/, ''),
  /** Admin API origin (no trailing slash), e.g. https://api.reflektme.app/subscriptions/admin */
  adminApiBase: (process.env.REACT_APP_ADMIN_API_BASE ?? '').replace(/\/+$/, ''),
  /** Full URL of the public Support HTTP route (no trailing slash), e.g. `https://api.reflektme.app/subscriptions/support`. GET/POST append `?clientId=…`. */
  supportApiUrl: process.env.REACT_APP_SUPPORT_API_URL ?? '',
  /**
   * Client product media only (`ProductMedia` on client Product page): when true, rollout env applies to all clients; when false, only IDs in `cloudinaryProductUploadTesters`.
   */
  cloudinaryProductUploadReleased: envFlagTrue(process.env.REACT_APP_CLOUDINARY_PRODUCT_UPLOAD_RELEASED ?? ''),
  /** Comma-separated client user IDs for Cloudinary product upload when `cloudinaryProductUploadReleased` is false. */
  cloudinaryProductUploadTesters: process.env.REACT_APP_CLOUDINARY_PRODUCT_UPLOAD_TESTERS ?? '',
  /** `cloudinary` = Cloudinary widget for users who pass the gate; otherwise legacy file picker + same GraphQL `uploadMedia`. */
  cloudinaryProductUploadMode:
    (process.env.REACT_APP_CLOUDINARY_PRODUCT_UPLOAD_MODE ?? 'local').trim().toLowerCase() === 'cloudinary'
      ? 'cloudinary'
      : 'local',
  /**
   * Client Gallery page — bulk / library staging upload: separate rollout from product media.
   * When true, all signed-in clients who can open Gallery see the control; when false, only `cloudinaryGalleryLibraryUploadTesters`.
   */
  cloudinaryGalleryLibraryUploadReleased: envFlagTrue(process.env.REACT_APP_CLOUDINARY_GALLERY_LIBRARY_UPLOAD_RELEASED ?? ''),
  /** Comma-separated client user IDs when gallery library upload is not released. */
  cloudinaryGalleryLibraryUploadTesters: process.env.REACT_APP_CLOUDINARY_GALLERY_LIBRARY_UPLOAD_TESTERS ?? '',
  /** `cloudinary` = Cloudinary widget for users who pass the gallery gate; `local` = native file picker (same allowed types). */
  cloudinaryGalleryLibraryUploadMode:
    (process.env.REACT_APP_CLOUDINARY_GALLERY_LIBRARY_UPLOAD_MODE ?? 'local').trim().toLowerCase() === 'cloudinary'
      ? 'cloudinary'
      : 'local',
  /**
   * Optional HTTP API for Gallery library upload smoke tests (e.g. API Gateway + Lambda).
   * When empty, Gallery uses GraphQL mutation `uploadMediaLibraryAssets`.
   */
  /**
   * Base URL for media-library Lambda (e.g. `https://api.reflektme.app/subscriptions`).
   * POST target is `{url}/{galleryLibraryUploadApiPath}` (default `galleryAPI`).
   */
  galleryLibraryUploadApiUrl: process.env.REACT_APP_GALLERY_LIBRARY_UPLOAD_API_URL ?? '',
  /** Path segment after base URL; Lambda maps `galleryAPI` → library assets (default `galleryAPI`). */
  galleryLibraryUploadApiPath: process.env.REACT_APP_GALLERY_LIBRARY_UPLOAD_API_PATH ?? 'galleryAPI',
  /**
   * `true` / `1` / `yes`: post to `{path}/test` (Lambda test route, no DB write when ENABLE_TEST_ROUTE).
   */
  galleryLibraryUploadUseTestPath: envFlagTrue(process.env.REACT_APP_GALLERY_LIBRARY_UPLOAD_USE_TEST_PATH ?? ''),
  /** Optional: must match Lambda INTERNAL_API_SECRET when that env is set. */
  galleryLibraryUploadInternalSecret: process.env.REACT_APP_GALLERY_LIBRARY_UPLOAD_INTERNAL_SECRET ?? '',
  /** gallery-api base (library drafts grid). Default: portal proxy to Fly gallery-api. */
  galleryApiUrl:
    process.env.REACT_APP_GALLERY_API_URL?.trim() || 'https://portal.reflektme.app/gallery-api',
  /** Product/variant rename API base; defaults to galleryApiUrl when unset. */
  catalogRenameApiUrl: process.env.REACT_APP_CATALOG_RENAME_API_URL?.trim() || '',
  /**
   * Catalog rename + manual create (Add product / Add variant): when true, all clients see edit controls; when false, only `catalogEditTesters`.
   */
  catalogEditReleased: envFlagTrue(process.env.REACT_APP_CATALOG_EDIT_RELEASED ?? ''),
  /** Comma-separated client user IDs when catalog edit/create is not released. */
  catalogEditTesters: process.env.REACT_APP_CATALOG_EDIT_TESTERS ?? '',
  /** Library tab on Gallery page: when true, all clients see it; else `galleryLibraryPageTesters`. */
  galleryLibraryPageReleased: envFlagTrue(process.env.REACT_APP_GALLERY_LIBRARY_PAGE_RELEASED ?? ''),
  galleryLibraryPageTesters: process.env.REACT_APP_GALLERY_LIBRARY_PAGE_TESTERS ?? '',
  /** AI description/tags in Media Preview: when true, all library users; else `galleryAiTesters`. */
  galleryAiReleased: envFlagTrue(process.env.REACT_APP_GALLERY_AI_RELEASED ?? ''),
  galleryAiTesters: process.env.REACT_APP_GALLERY_AI_TESTERS ?? '',
  /**
   * 2026 brand assets in portal (logo / widget CTA preview): when true, everyone including guests;
   * when false, only `rebrandTesters` clientPids. Storefront button is CDN-only (not this flag).
   */
  rebrandReleased: envFlagTrue(process.env.REACT_APP_REBRAND_RELEASED ?? ''),
  /** Comma-separated clientPid (`user.id`) when rebrand is not fully released. */
  rebrandTesters: process.env.REACT_APP_REBRAND_TESTERS ?? '',
  /**
   * Per image-build cache bust (Dockerfile writes REACT_APP_ASSET_HASH).
   * Used for webpack-dev bundle names and public rebrand asset query strings.
   */
  assetHash: process.env.REACT_APP_ASSET_HASH || '0',
  cloudinaryCloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ?? '',
  cloudinaryUploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET ?? '',
  /**
   * Comma-separated Cloudinary upload widget `sources` keys to omit (e.g. `facebook,instagram`).
   * Keys are normalized: trimmed, lowercased, internal spaces → `_`. Unknown tokens are ignored.
   */
  cloudinaryUploadWidgetExcludeSources: process.env.REACT_APP_CLOUDINARY_UPLOAD_WIDGET_EXCLUDE_SOURCES ?? '',
  /** Google Custom Search API key for Cloudinary widget `image_search` (see Cloudinary upload widget docs). */
  cloudinaryImageSearchGoogleApiKey: process.env.REACT_APP_CLOUDINARY_IMAGE_SEARCH_GOOGLE_API_KEY ?? '',
  /** Dropbox app key when using widget `dropbox` source (HTTPS required). */
  cloudinaryDropboxAppKey: process.env.REACT_APP_CLOUDINARY_DROPBOX_APP_KEY ?? '',
  /** Optional Google Drive OAuth client ID; omit to use Cloudinary’s default app. */
  cloudinaryGoogleDriveClientId: process.env.REACT_APP_CLOUDINARY_GOOGLE_DRIVE_CLIENT_ID ?? '',
  authTokenKey: 'api_key',
  authRefreshKey: 'api_refresh_key',
  defaultDateFormat: 'MM-DD-YYYY',
  supportedDateFormats: [
    {
      label: 'DD-MM-YYYY',
      value: 'DD-MM-YYYY',
    },
    {
      label: 'MM-DD-YYYY',
      value: 'MM-DD-YYYY',
    },
  ],
  modalRootId: 'modal-root',
  popupsRootId: 'popups-root',
  supportedCouponFileType: ['pdf', 'txt', 'png', 'jpg', 'jpeg', 'doc', 'docx'],
  errorCodes: {
    userNotApproved: 'USER_NOT_APPROVED',
    emailNotApproved: 'EMAIL_NOT_APPROVED',
    userRejected: 'USER_REJECTED',
    emailNotConfirm: 'EMAIL_NOT_CONFIRMED',
    unauthenticated: 'UNAUTHENTICATED',
    productNotExist: 'PRODUCT_NOT_EXISTS',
    accessDenied: 'ACCESS_DENIED',
  },
  legalPage: {
    termsOfUse: {
      label: 'Terms of Use',
      link: 'https://www.reflektme.com/terms-of-use',
    },
    privacyPolicy: {
      label: 'Privacy Policy',
      link: 'https://www.reflektme.com/privacy-policy',
    },
    statusPage: {
      label: 'Status Page',
      link: 'https://statuspage.reflektme.app',
    },
    Wiki: {
      label: 'Wiki Page',
      link: 'https://www.reflektme.com/reflekt-me-wiki',
    },
    technicalSupport: {
      label: 'Technical Support',
      link: 'https://forms.gle/kF3YAvrpt5PGZpKt7',
    },
    expertHours: {
      label: 'Expert Hours',
      link: 'https://forms.gle/1QUEYzPNXSWCiWum6',
    },
    contentCreation: {
      label: 'Content Creation Request',
      link: 'https://forms.gle/1mgVANe6ntSnRU6v6',
    },
    subscriptionsPage: {
      label: 'Upgrade Plan',
      link: 'https://www.reflektme.com/our-app',
    },
  },
}
